/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { I18nService } from '@recruitee/i18n';
import { BillingPlanTier } from '@recruitee/products-types';

type CompareList = Array<{
  title: string;
  items: Array<{ title: string; checklist: boolean[]; tooltip?: string }>;
}>;

type Checklist = { [key: string]: string[] };

@Injectable({ providedIn: 'root' })
export class PlanPickerModel {
  public checklist: Checklist = {
    essentials: [],
    automation: [],
    optimization: [],
    additionalServices: [],
    compliance: [],
  };
  public comparisons: CompareList = [];
  public featuresByCodename = this.getFeaturesByCodename();

  constructor(
    private i18n: I18nService,
    private store: Store,
  ) {
    this.init();
  }

  private init() {
    this.checklist = { ...this._checklist };
    this.comparisons = [...this._comparisons];
    this.featuresByCodename = this.getFeaturesByCodename();
  }

  private get _checklist(): { [key: string]: string[] } {
    return {
      essentials: [
        this.i18n.translate('app.pricing.plan_picker.features.user_friendly_pipelines'),
        this.i18n.translate('app.pricing.plan_picker.features.job_boards'),
        this.i18n.translate('app.pricing.plan_picker.features.smart_campaigns'),
        this.i18n.translate('app.pricing.plan_picker.features.careers_hub'),
        this.i18n.translate('app.pricing.plan_picker.features.jobs_widget'),
        this.i18n.translate('app.pricing.plan_picker.features.candidate_video_chat'),
        this.i18n.translate('app.pricing.plan_picker.features.candidate_overdue_reminders'),
        this.i18n.translate('app.pricing.plan_picker.features.gdpr'),
        this.i18n.translate('app.pricing.plan_picker.features.two_factor_authentication'),
        this.i18n.translate('app.pricing.plan_picker.features.mobile_app'),
        this.i18n.translate('app.pricing.plan_picker.features.unlimited_users'),
        this.i18n.translate('app.pricing.plan_picker.features.email_cal_sync'),
        this.i18n.translate('app.pricing.plan_picker.features.ta_chrome_widget'),
        this.i18n.translate('app.pricing.plan_picker.features.marketplace_access2'),
        this.i18n.translate('app.pricing.plan_picker.features.chat_and_support'),
        this.i18n.translate('app.pricing.plan_picker.features.essential_dashboards'),
        this.i18n.translate('app.pricing.plan_picker.features.api_access'),
      ],
      automation: [
        this.i18n.translate('app.pricing.plan_picker.features.automated_actions'),
        this.i18n.translate('app.pricing.plan_picker.features.send_later'),
        this.i18n.translate('app.pricing.plan_picker.features.schedulers'),
        this.i18n.translate('app.pricing.plan_picker.features.knockout_questions'),
        this.i18n.translate('app.pricing.plan_picker.features.calendar_meeting_rooms'),
        this.i18n.translate('app.pricing.plan_picker.features.workflow_templates'),
        this.i18n.translate('app.pricing.plan_picker.features.gdpr_automations'),
        this.i18n.translate('app.pricing.plan_picker.features.customizable_report_builder'),
        this.i18n.translate('app.pricing.plan_picker.features.webhooks_access'),
      ],
      optimization: [
        this.i18n.translate('app.pricing.plan_picker.features.custom_career_url'),
        this.i18n.translate('app.pricing.plan_picker.features.access_roles'),
        this.i18n.translate('app.pricing.plan_picker.features.multi_lang'),
        this.i18n.translate('app.pricing.plan_picker.features.careers_hub_multi_page'),
        this.i18n.translate('app.pricing.plan_picker.features.eeo'),
        this.i18n.translate('app.pricing.plan_picker.features.fair_evaluations'),
        this.i18n.translate('app.pricing.plan_picker.features.locations'),
      ],
      additionalServices: [
        this.i18n.translate('app.pricing.plan_picker.features.requisition_approvals'),
        this.i18n.translate('app.pricing.plan_picker.features.api_support'),
        this.i18n.translate('app.pricing.plan_picker.features.customer_success_rep'),
        this.i18n.translate('app.pricing.plan_picker.features.data_imports'),
        this.i18n.translate('app.pricing.plan_picker.features.tailored_onboarding_rollout_plans'),
        this.i18n.translate('app.pricing.plan_picker.features.team_consultations'),
        this.i18n.translate('app.pricing.plan_picker.features.sandbox'),
        // Referrals are in beta
        // this.i18n.translate('app.pricing.plan_picker.features.referrals'),
        this.i18n.translate('app.pricing.plan_picker.features.referrals_beta'),
      ],
      compliance: [
        this.i18n.translate('app.pricing.plan_picker.features.custom_contracts'),
        // SLA not ready yet
        // this.i18n.translate('app.pricing.plan_picker.features.sla'),
        this.i18n.translate('app.pricing.plan_picker.features.sso'),
        this.i18n.translate('app.pricing.plan_picker.features.more_security'),
        this.i18n.translate('app.pricing.plan_picker.features.bi_connector'),
        this.i18n.translate('app.pricing.plan_picker.features.locations'),
      ],
    };
  }

  private get _comparisons(): CompareList {
    return [
      {
        title: this.i18n.translate('app.pricing.plan_picker.features.organize_your_recruitment'),
        items: [
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.user_friendly_pipelines'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.job_boards'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.smart_campaigns'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.live_chat_support'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.careers_hub'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.jobs_widget'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.candidate_video_chat'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate(
              'app.pricing.plan_picker.features.candidate_overdue_reminders',
            ),
            checklist: [true, true, true],
            tooltip: this.i18n.translate(
              'app.pricing.plan_picker.features_tooltip.candidate_overdue_reminders',
            ),
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.gdpr'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate(
              'app.pricing.plan_picker.features.two_factor_authentication',
            ),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.mobile_app'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.unlimited_users'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.email_cal_sync'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.ta_chrome_widget'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.email_templates'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.marketplace_access'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.social_media_connections'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate(
              'app.pricing.plan_picker.features.team_notes_tasks_mentions',
            ),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.resume_parser'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.multiple_talent_pools'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.essential_dashboards'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.profile_print'),
            checklist: [false, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.eeo'),
            checklist: [false, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.careers_hub_multi_page'),
            checklist: [false, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.fair_evaluations'),
            checklist: [false, true, true],
            tooltip: this.i18n.translate(
              'app.pricing.plan_picker.features_tooltip.fair_evaluations',
            ),
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.locations'),
            checklist: [false, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.requisition_approvals'),
            checklist: [false, false, true],
            tooltip: this.i18n.translate(
              'app.pricing.plan_picker.features_tooltip.requisition_approvals',
            ),
          },
        ],
      },
      {
        title: this.i18n.translate('app.pricing.plan_picker.features.automate_workflows'),
        items: [
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.automated_actions'),
            checklist: [false, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.send_later'),
            checklist: [false, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.schedulers2'),
            checklist: [false, true, true],
            tooltip: this.i18n.translate('app.pricing.plan_picker.features_tooltip.schedulers'),
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.workflow_templates'),
            checklist: [false, true, true],
            tooltip: this.i18n.translate(
              'app.pricing.plan_picker.features_tooltip.workflow_templates',
            ),
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.gdpr_automations'),
            checklist: [false, true, true],
            tooltip: this.i18n.translate(
              'app.pricing.plan_picker.features_tooltip.gdpr_automations',
            ),
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.api_access'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.webhooks_access'),
            checklist: [false, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.knockout_questions'),
            checklist: [false, true, true],
          },
        ],
      },
      {
        title: this.i18n.translate('app.pricing.plan_picker.features.optimize_practices'),
        items: [
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.custom_career_url'),
            checklist: [false, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.access_roles'),
            checklist: [false, true, true],
            tooltip: this.i18n.translate('app.pricing.plan_picker.features_tooltip.access_roles'),
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.multi_lang'),
            checklist: [false, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.calendar_meeting_rooms'),
            checklist: [false, true, true],
            tooltip: this.i18n.translate(
              'app.pricing.plan_picker.features_tooltip.calendar_meeting_rooms',
            ),
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.custom_views_and_fields'),
            checklist: [false, true, true],
          },
          {
            title: this.i18n.translate(
              'app.pricing.plan_picker.features.questionnaires_and_evaluation_forms',
            ),
            checklist: [false, true, true],
          },
          {
            title: this.i18n.translate(
              'app.pricing.plan_picker.features.customizable_report_builder',
            ),
            checklist: [false, true, true],
          },
        ],
      },
      {
        title: this.i18n.translate('app.pricing.plan_picker.features.support_and_services'),
        items: [
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.api_support'),
            checklist: [false, false, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.customer_success_rep'),
            checklist: [false, false, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.data_imports'),
            checklist: [false, false, true],
          },
          {
            title: this.i18n.translate(
              'app.pricing.plan_picker.features.tailored_onboarding_rollout_plans',
            ),
            checklist: [false, false, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.team_consultations'),
            checklist: [false, false, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.sandbox'),
            checklist: [false, false, true],
          },
          {
            // Referrals are in beta
            // title: this.i18n.translate('app.pricing.plan_picker.features.referrals'),
            title: this.i18n.translate('app.pricing.plan_picker.features.referrals_beta'),
            checklist: [false, false, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.bi_connector'),
            checklist: [false, false, true],
          },
        ],
      },
      {
        title: this.i18n.translate('app.pricing.plan_picker.features.enhanced_compliance'),
        items: [
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.audit_logs'),
            checklist: [true, true, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.custom_contracts'),
            checklist: [false, false, true],
          },
          // SLA not ready yet
          // {
          //   title: this.i18n.translate('app.pricing.plan_picker.features.sla'),
          //   checklist: [false, false, true],
          // },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.sso'),
            checklist: [false, false, true],
          },
          {
            title: this.i18n.translate('app.pricing.plan_picker.features.more_security'),
            // eslint-disable-next-line max-lines
            checklist: [false, false, true],
            // eslint-disable-next-line max-lines
          },
        ],
      },
    ];
  }

  private getFeaturesByCodename() {
    return {
      [BillingPlanTier.Launch]: {
        label: this.i18n.translate('app.pricing.plan_picker.features.recruitment_essentials'),
        features: this.checklist.essentials,
      },
      [BillingPlanTier.Scale]: {
        label: this.i18n.translate('app.pricing.plan_picker.features.all_launch_plus'),
        features: [...this.checklist.automation, ...this.checklist.optimization],
      },
      [BillingPlanTier.Lead]: {
        label: this.i18n.translate('app.pricing.plan_picker.features.all_scale_plus'),
        features: [...this.checklist.additionalServices, ...this.checklist.compliance],
      },
      [BillingPlanTier.Start]: {
        label: this.i18n.translate('app.pricing.plan_picker.features.recruitment_essentials'),
        features: this.checklist.essentials,
      },
      [BillingPlanTier.Grow]: {
        label: this.i18n.translate('app.pricing.plan_picker.features.all_launch_plus'),
        features: [...this.checklist.automation, ...this.checklist.optimization],
      },
      [BillingPlanTier.Optimize]: {
        label: this.i18n.translate('app.pricing.plan_picker.features.all_scale_plus'),
        features: [...this.checklist.additionalServices, ...this.checklist.compliance],
      },
    };
  }
}
